import React, { useMemo, useState } from 'react';
import { ReactComponent as IconReport } from '../../../assets/icons/u_reports.svg';
import exclamationCircle from '../../../assets/icons/u_exclamation-circle.svg';
import closeIcon from '../../../assets/icons/u_multiply-1.svg';
import { useTranslation } from 'react-i18next';
import './ReportsDownloadButton.scss';
import {
  generateOnePagerReportsBatch,
  OnePagerBatchProgress,
  OnePagerBatchResult,
  OnePagerBatchSuccess,
} from '../../Projects/ProjectsReport/utils/Downloaders/OnePagerReportDownload';
import BaseModal from '../Modals/BaseModal';
import { styles } from '../Modals/Styles';
import AutoDismissAlert from '../../Projects/ProjectsReport/view/Common/AlertTooltips';
import { ERROR_IN_GENERATE_REPORT, SUCESSFUL_GENERATED_PROJECT_REPORT } from '../../Projects/ProjectsReport/utils/Constants/AlertStatusConstants';
import { zip } from 'fflate';

interface Props {
  reviewProjectsSelectedRows: any[];
  zipFilename: string | (() => string);
}

const initialProgress: OnePagerBatchProgress = {
  total: 0,
  processed: 0,
  percent: 0,
  successCount: 0,
  failureCount: 0,
};

const initialResult: OnePagerBatchResult = {
  successes: [],
  failures: [],
  total: 0,
};

const downloadBlob = (blob: Blob, filename: string) => {
  const fileUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = fileUrl;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(fileUrl);
};

const createReportsZip = async (
  reports: OnePagerBatchSuccess[],
): Promise<Blob> => {
  const zipFiles: { [filename: string]: Uint8Array } = {};

  await Promise.all(
    reports.map(async report => {
      const arrayBuffer = await report.blob.arrayBuffer();
      zipFiles[report.filename] = new Uint8Array(arrayBuffer);
    }),
  );

  return new Promise<Blob>((resolve, reject) => {
    zip(
      zipFiles,
      {
        level: 0,
      },
      (error, compressedData) => {
        if (error) {
          reject(error);
          return;
        }

        resolve(
          new Blob([compressedData], {
            type: 'application/zip',
          }),
        );
      },
    );
  });
};

export const ReportsDownloadButton = (props: Props) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [phase, setPhase] = useState<'idle' | 'generating' | 'result'>('idle');
  const [isRetrying, setIsRetrying] = useState(false);
  const [progress, setProgress] = useState<OnePagerBatchProgress>(initialProgress);
  const [batchResult, setBatchResult] = useState<OnePagerBatchResult>(initialResult);
  const [alertConfig, setAlertConfig] = useState<any>();

  const successfulReportsCount = useMemo(() => batchResult.successes.length, [batchResult.successes.length]);
  const failedReportsCount = useMemo(() => batchResult.failures.length, [batchResult.failures.length]);

  const runBatchGeneration = async (projectIds: number[]) => {
    setPhase('generating');
    setProgress({
      total: projectIds.length,
      processed: 0,
      percent: 0,
      successCount: 0,
      failureCount: 0,
    });

    const result = await generateOnePagerReportsBatch(projectIds, {
      onProgress: setProgress,
    });

    return result;
  };

  const isCompleteSuccess = (result: OnePagerBatchResult) => (
    result.total > 0 && result.failures.length === 0 && result.successes.length === result.total
  );

  const downloadReportsZip = async (
    reports: OnePagerBatchSuccess[],
  ): Promise<void> => {
    const zipBlob = await createReportsZip(reports);

    const zipFilename = typeof props.zipFilename === 'function'
      ? props.zipFilename()
      : props.zipFilename;

    downloadBlob(zipBlob, zipFilename);
  };

  const handleDownloadReports = async () => {
    if (!props.reviewProjectsSelectedRows?.length) return;
    setIsModalOpen(true);
    try {
      const result = await runBatchGeneration(props.reviewProjectsSelectedRows);
      if (isCompleteSuccess(result)) {
        await downloadReportsZip(result.successes);
        setIsModalOpen(false);
        setPhase('idle');
        setProgress(initialProgress);
        setBatchResult(initialResult);
        setAlertConfig(SUCESSFUL_GENERATED_PROJECT_REPORT);
        return;
      }

      setBatchResult(result);
      setPhase('result');
    } catch (error) {
      setIsModalOpen(false);
      setAlertConfig(ERROR_IN_GENERATE_REPORT);
      console.error('Error downloading reports', error);
    }
  };

  const handleRetryFailed = async () => {
    if (!batchResult.failures.length) return;

    setIsRetrying(true);
    const failedProjectIds = batchResult.failures.map(item => item.projectId);

    try {
      const retryResult = await runBatchGeneration(failedProjectIds);

      const mergedSuccessMap = new Map<number, OnePagerBatchSuccess>();
      batchResult.successes.forEach(item => mergedSuccessMap.set(item.projectId, item));
      retryResult.successes.forEach(item => mergedSuccessMap.set(item.projectId, item));

      const retryFailureIds = new Set(retryResult.failures.map(item => item.projectId));
      const mergedFailures = retryResult.failures.filter(item => retryFailureIds.has(item.projectId));

      const mergedResult = {
        successes: Array.from(mergedSuccessMap.values()),
        failures: mergedFailures,
        total: batchResult.total,
      };

      if (isCompleteSuccess(mergedResult)) {
        await downloadReportsZip(mergedResult.successes);
        setIsModalOpen(false);
        setPhase('idle');
        setProgress(initialProgress);
        setBatchResult(initialResult);
        setAlertConfig(SUCESSFUL_GENERATED_PROJECT_REPORT);
        return;
      }

      setBatchResult(mergedResult);
      setPhase('result');
    } catch (error) {
      setAlertConfig(ERROR_IN_GENERATE_REPORT);
      setPhase('result');
      console.error('Error retrying failed reports', error);
    } finally {
      setIsRetrying(false);
    }
  };

  const handleDownloadGenerated = async () => {
    if (!batchResult.successes.length) {
      setAlertConfig(ERROR_IN_GENERATE_REPORT);
      return;
    }

    try {
      await downloadReportsZip(batchResult.successes);
      setAlertConfig(SUCESSFUL_GENERATED_PROJECT_REPORT);
      handleCloseModal()
    } catch (error) {
      setAlertConfig(ERROR_IN_GENERATE_REPORT);
      console.error('Error downloading generated reports', error);
      return;
    }
  };

  const handleCloseModal = () => {
    if (phase === 'generating') return;
    setIsModalOpen(false);
    setPhase('idle');
    setProgress(initialProgress);
    setBatchResult(initialResult);
  };

  return (
    <>
      <button
        className={`btn-primary reports-download-button${props.reviewProjectsSelectedRows.length > 0 ? ' show' : ''}`}
        onClick={handleDownloadReports}
      >
        <IconReport />{' '}{t('download_reports')}
      </button>

      <BaseModal
        isOpen={isModalOpen}
        style={styles}
        className="reports-download-status-modal"
        shouldCloseOnOverlayClick={false}
      >
        {phase === 'generating' && (
          <div className="reports-download-content p-4">
            <div className="reports-download-progress-row">
              <div className="reports-download-progress-track">
                <div className="reports-download-progress-bar" style={{ width: `${progress.percent}%` }} />
              </div>
              <div className="reports-download-percent">{progress.percent}%</div>
            </div>
            <h3>{t('creating_reports')}</h3>
          </div>
        )}

        {phase === 'result' && (
          <div className="reports-download-content reports-download-result">
            <button className="reports-download-close" onClick={handleCloseModal}>
              <img src={closeIcon} alt="Close" />
            </button>
            <div >
              <img className='exclamation-icon-style' src={exclamationCircle} alt="Exclamation Circle" />
            </div>
            <h3>{`${successfulReportsCount} ${t('number_of_reports_generated')} ${batchResult.total} ${t('reports_generated')}`}</h3>
            {failedReportsCount > 0 && (
              <>
                <p>{t('reports_not_generated')}</p>
                <ul>
                  {batchResult.failures.map(item => (
                    <li key={item.projectId}>{item.projectCode}</li>
                  ))}
                </ul>
              </>
            )}
            <p className='mt-2'>{`${t('retry_download')} ${successfulReportsCount} ${t('retry_download_available')}`}</p>
            <div className="reports-download-actions">
              <button
                className="btn-primary-outlined"
                onClick={handleRetryFailed}
                disabled={isRetrying || failedReportsCount === 0}
              >
                {`${t('retry_message')} ${failedReportsCount} ${t('failed_download_reports')}`}
              </button>
              <button
                className="btn-primary"
                onClick={handleDownloadGenerated}
                disabled={successfulReportsCount === 0}
              >
                {`${t('download')} ${successfulReportsCount} ${t('menu_reports')}`}
              </button>
            </div>
          </div>
        )}
      </BaseModal>

      {alertConfig && (
        <AutoDismissAlert
          message={alertConfig.message}
          title={alertConfig.title}
          severity={alertConfig.severity}
          duration={alertConfig.duration}
          onClose={() => setAlertConfig(null)}
        />
      )}
    </>
  );
};
